<script lang="ts">
    import FieldHeading from './field-heading.svelte'

    export let label: string
    export let data: Record<string, any>
    export let value: string[] = []
    export let required = false
    export let readOnly = false

    let otherChecked = false
    let otherText = ''
    // Último `value` que este componente produziu — comparado por referência, já que toda escrita
    // aqui cria um array novo. Distingue "mudou lá fora" de "mudou porque estamos digitando".
    let selfWrittenValue: string[] | null = null

    // Reidrata o campo "Outro" a partir do valor salvo — sem isso, reabrir um preenchimento com
    // resposta customizada mostrava o campo em branco.
    //
    // Só reage a valor vindo de fora, e de propósito não depende de `otherText`: a versão anterior
    // relia em `otherText` e regravava esse mesmo campo a cada tecla. Como o `syncOther` grava a
    // versão com `trim()`, o texto em digitação era sobrescrito pela versão aparada e o campo
    // travava — na prática só a primeira letra entrava.
    $: rehydrateFromValue(value, data?.options)

    function rehydrateFromValue(currentValue: string[], options: string[] | undefined) {
        if (currentValue === selfWrittenValue) return
        selfWrittenValue = currentValue
        const predefined = options ?? []
        const extra = (currentValue ?? []).find((item) => !predefined.includes(item))
        if (extra !== undefined) {
            otherChecked = true
            otherText = extra
        }
    }

    /** Toda escrita passa por aqui, para o bloco reativo acima reconhecer o valor como nosso. */
    function writeValue(newValue: string[]) {
        selfWrittenValue = newValue
        value = newValue
    }

    function toggle(option: string, checked: boolean) {
        if (readOnly) return
        writeValue(checked ? [...value, option] : value.filter((v) => v !== option))
    }

    function syncOther() {
        if (readOnly) return
        const predefined = data?.options ?? []
        const withoutOther = value.filter((v) => predefined.includes(v))
        writeValue(otherChecked && otherText.trim() ? [...withoutOther, otherText.trim()] : withoutOther)
    }

    function onOtherToggle(checked: boolean) {
        if (readOnly) return
        otherChecked = checked
        syncOther()
    }

    function onOtherTextInput() {
        if (!otherChecked || readOnly) return
        syncOther()
    }
</script>

<div class="view-field">
    <FieldHeading {label} {required} {readOnly} />
    <div class="view-options">
        {#each data?.options ?? [] as option (option)}
            <label class="view-option" class:disabled={readOnly} class:selected={value.includes(option)}>
                <input
                    type="checkbox"
                    checked={value.includes(option)}
                    disabled={readOnly}
                    on:change={(event) => toggle(option, event.currentTarget.checked)}
                />
                <span class="option-label">{option}</span>
            </label>
        {/each}

        {#if data?.allowOther}
            <label class="view-option other-option" class:disabled={readOnly} class:selected={otherChecked}>
                <input type="checkbox" checked={otherChecked} disabled={readOnly} on:change={(event) => onOtherToggle(event.currentTarget.checked)} />
                <span class="option-label">{data?.otherLabel || 'Outro'}:</span>
                <input
                    type="text"
                    class="other-input"
                    placeholder="Digite uma opção"
                    bind:value={otherText}
                    disabled={readOnly || !otherChecked}
                    on:input={onOtherTextInput}
                />
            </label>
        {/if}
    </div>
</div>

<style>
    .view-options {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
        gap: 8px;
        width: 100%;
    }
    .view-option {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        border-radius: 8px;
        background: #f8f9fa;
        cursor: pointer;
        min-width: 0;
    }
    .view-option.disabled {
        cursor: not-allowed;
    }
    .view-option.selected {
        background: var(--cs2-accent-hover, #17be5e);
    }
    .view-option input[type='checkbox'] {
        width: 16px;
        height: 16px;
        accent-color: var(--cs2-accent, #1bdf6f);
        flex-shrink: 0;
    }
    .option-label {
        min-width: 0;
        font-weight: 600;
        font-size: 0.85rem;
        color: #212529;
    }
    /* Opção não marcada em documento travado: atenuada, mas ainda legível. O cinza claro que estava
       aqui dava ~2:1 sobre o fundo do card — em modo leitura, que é justamente quando o documento
       precisa ser lido, as opções ficavam quase invisíveis. */
    .view-option.disabled .option-label {
        color: #495057;
    }
    /* Depois da regra de `disabled` de propósito: as duas têm a mesma especificidade, e a marcada
       precisa vencer. Invertido, o texto ficava cinza sobre o verde (~1.2:1) — o caso que menos pode
       falhar, já que é justamente a resposta escolhida. */
    .view-option.selected .option-label,
    .view-option.selected.disabled .option-label {
        color: #1f2937;
    }
    .other-option {
        flex-wrap: wrap;
    }
    .other-input {
        flex: 1;
        min-width: 60px;
        border: none;
        border-bottom: 1px solid #ced4da;
        background: transparent;
        padding: 4px 2px;
        font-size: 0.85rem;
        font-weight: 500;
        color: #212529;
    }
    .other-input:focus {
        outline: none;
        border-bottom: 2px solid var(--cs2-accent, #4263eb);
    }
    .other-input:disabled {
        /* anula o !important do css_global.css (input:disabled) que pinta o fundo de cinza/escuro */
        background: transparent !important;
        color: #495057;
        cursor: not-allowed;
    }
    /* Aqui o texto digitado é a própria resposta, sobre o fundo verde — precisa do contraste cheio. */
    .view-option.selected .other-input:disabled {
        color: #1f2937;
    }
    .view-option.selected .other-input {
        border-bottom-color: #1f293766;
    }
</style>
